import * as React from 'react';
import { tabbable } from 'tabbable';
import { __assign } from 'tslib';
import { StackingComponentProps, StackingComponentWrapper } from '../../utils/StackingComponent';
import { getVisualViewport } from '../../utils/ViewportHelpers';
import { BorderWidth } from './DropdownButtonStyles';

export type DropdownOverlayProps = Pick<StackingComponentProps, 'portalEventTrapOverrides' | 'stackingRootId' | 'zIndex'> & {
  /** Which side of the anchor rect the popup should align to */
  alignment: 'left' | 'right';
  /** See `DialogProps.disableAutoFocus` */
  getElementToFocus?: (containerElement: HTMLElement) => HTMLElement;
  /** The rect the popup is positioned against */
  getAnchorClientRect: () => ClientRect;
};

export type DropdownOverlayState = {
  positionedAbove: boolean;
};

/**
 * * `true` when a dropdown popup is positioned above (rather than below) its button.
 * * `false` when below.
 * * `undefined` if a dropdown popup is not an ancestor of the component consuming this Context.
 */
export const DropdownPositionedAboveContext = React.createContext<boolean | undefined>(undefined);
DropdownPositionedAboveContext.displayName = 'DropdownPositionedAboveContext';

/** @private Internal, private implementation detail of the `DropdownButton` widget. Unsupported. Do not use. */
export class DropdownOverlay extends React.PureComponent<DropdownOverlayProps, DropdownOverlayState> {
  private ref: React.RefObject<StackingComponentWrapper> = React.createRef();

  public state: DropdownOverlayState = { positionedAbove: false };

  public componentDidMount(): void {
    this.reposition();

    // we don't want to focus the element until the styling/layout has settled and painted, otherwise the viewport tends to jump
    window.setTimeout(() => {
      this.focusFirstTabbable();
    });
  }

  public componentDidUpdate(prevProps: DropdownOverlayProps): void {
    if (prevProps.getAnchorClientRect !== this.props.getAnchorClientRect || prevProps.alignment !== this.props.alignment) {
      this.reposition();
    }
  }

  private focusFirstTabbable(): void {
    const mountPoint = this.ref.current?.getMountPoint();
    if (!mountPoint) { return; }

    const firstTabbable = this.props.getElementToFocus?.(mountPoint) ||
      // falls back to focusing the first focusable if there is nothing tabbable
      tabbable(mountPoint)[0] || mountPoint.querySelector('[tabindex]');

    firstTabbable.focus();
  }

  /**
   * Repositions the popup. Might be useful if the popup contents weren't fully sized at the time of attachment,
   * e.g. an image still loading. This is excluded from React render() path because we generally want it to stay put after attaching.
   */
  public reposition(): void {
    const positioningElement = this.ref.current!.getMountPoint();
    const newPosition = this.getPosition();
    __assign(positioningElement.style, newPosition);
    this.setState({ positionedAbove: !!newPosition.bottom });
  }

  private getPosition(): Partial<CSSStyleDeclaration> {
    const popupElem = this.ref.current!.getMountPoint();
    const { width: popupWidth, height: popupHeight } = popupElem.getBoundingClientRect();

    // it's called a dropdown, not a dropup, so prefer to attach the popup below, unless
    // (1) doesn't fit below and (2) there is more space above than below
    const anchorRect = this.props.getAnchorClientRect();
    const viewport = getVisualViewport();
    const spaceBelow = viewport.offsetTop + viewport.height - anchorRect.bottom;
    const spaceAbove = anchorRect.top - viewport.offsetTop;
    // 'top'/'bottom' here read unintuitively but they refer to the css property, so attaching above the anchor means using 'bottom'
    const verticalAttachment = (popupHeight > spaceBelow && spaceAbove > spaceBelow) ? 'bottom' : 'top';

    // we allow an external preference for left vs right alignment via a prop, but still switch if we would end up out-of-bounds
    const fitsLeft = anchorRect.left + popupWidth < viewport.offsetLeft + viewport.width;
    const fitsRight = anchorRect.right - popupWidth > viewport.offsetLeft;
    let { alignment: horizontalAttachment } = this.props;
    if (horizontalAttachment === 'left' && !fitsLeft && fitsRight) {
      horizontalAttachment = 'right';
    } else if (horizontalAttachment === 'right' && !fitsRight && fitsLeft) {
      horizontalAttachment = 'left';
    }

    // we can't know apriori what the nearest positioned ancestor is (or if we can, it's too nasty to figure out), so instead
    // we attach the popup and then calculate the necessary repositioning relative to where it first appears
    popupElem.style[horizontalAttachment] = '0';
    popupElem.style[verticalAttachment] = '0';
    const popupRect = popupElem.getBoundingClientRect();

    // we always set all four properties because we need to clear all previous positioning, otherwise we'll be specifying the whole
    // popup rectangle rather than just its attachment+position
    const positionStyle: Partial<CSSStyleDeclaration> = {
      top: verticalAttachment === 'top' ? anchorRect.bottom - popupRect.top - BorderWidth + 'px' : '',
      bottom: verticalAttachment === 'bottom' ? popupRect.bottom - anchorRect.top - BorderWidth + 'px' : '',
      left: horizontalAttachment === 'left' ? anchorRect.left - popupRect.left + 'px' : '',
      right: horizontalAttachment === 'right' ? popupRect.right - anchorRect.right + 'px' : '',
    };

    return positionStyle;
  }

  public render(): React.ReactElement {
    const { alignment, children, getAnchorClientRect, ...stackingComponentProps } = this.props;
    return (
      <StackingComponentWrapper {...stackingComponentProps} ref={this.ref} wrapperTestId='DropdownOverlay'>
        <DropdownPositionedAboveContext.Provider value={this.state.positionedAbove}>{children}</DropdownPositionedAboveContext.Provider>
      </StackingComponentWrapper>
    );
  }
}
